<template>
    <central-modal
        header="Contact our experts"
        :visible="props.visible"
        @update:visible="handleVisibilityUpdate"
    >
        <div class="contact-us flex-column-with-gap">
            <our-experts-section :our-experts="ourExperts" />
            <div class="email-input" v-if="!authState.loggedIn">
                <label for="email">Your email address:</label>
                <primevue-textarea
                    auto-resize
                    id="email"
                    maxlength="100"
                    v-model="requesterEmailForPublicUser"
                    rows="1"
                    cols="40"
                />
            </div>
            <primevue-textarea
                auto-resize
                maxlength="500"
                placeholder="What would you like to ask?"
                v-model="feedbackMessage"
                rows="5"
                cols="50"
            />
        </div>
        <template #footer>
            <primevue-button
                label="Submit"
                :disabled="feedbackSubmitDisabled"
                @click="contactUs" />
        </template>
    </central-modal>
</template>

<script lang="ts" setup>
import { IChatToExpertsPayload } from '@/interfaces/deltaGpt/IChatToExpertsPayload.ts';
import { computed, ref } from 'vue';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { publishToast } from '@/utils/Toast.ts';
import { IExpert } from '@/interfaces/deltaGpt/IExpert.ts';
import { useToast } from 'primevue';
import PrimevueTextarea from 'primevue/textarea';
import primevueButton from 'primevue/button';
import { useCentralModalStore } from '@/stores/centralModalStore.ts';
import { authState } from '@lcp/http-auth';
import OurExpertsSection from './OurExperts.vue';
import CentralModal from '../common/CentralModal.vue';

const toast = useToast();
const centralModalStore = useCentralModalStore();

const props = defineProps<{
    visible: boolean;
    documentTitle: string;
    ourExperts: Array<IExpert>;
}>();

const emit = defineEmits<{
    'update:visible': [boolean];
    'close': [];
}>();

const feedbackMessage = ref<string>('');
const requesterEmailForPublicUser = ref<string>('');

function closeModal (): void {
    centralModalStore.closeCurrentModal();
    emit('update:visible', false);
    emit('close');
}

function handleVisibilityUpdate (visible: boolean): void {
    emit('update:visible', visible);
    if (!visible) emit('close');
}

const feedbackSubmitDisabled = computed<boolean>(() => {
    const noFeedbackMessage = feedbackMessage.value?.trim() === '';
    if (noFeedbackMessage) return true;
    if (authState.value.loggedIn) return false;
    return !requesterEmailForPublicUser.value;
});

async function contactUs (): Promise<void> {
    const chatPayload: IChatToExpertsPayload = {
        question: feedbackMessage.value,
        documentName: props.documentTitle,
        emails: props.ourExperts.map(expert => expert.email),
        requesterEmail: requesterEmailForPublicUser.value ?? null,
    };

    try {
        await deltaGptApi.contactOurExperts(chatPayload);
        publishToast(toast, 'Feedback submitted', 'success');
        closeModal();
    } catch {
        publishToast(toast, 'Unable to submit feedback', 'error');
    }

    feedbackMessage.value = '';
    requesterEmailForPublicUser.value = '';
}
</script>

<style lang="scss" scoped>
.contact-us {
    gap: var(--largeSpacing);
    padding: var(--defaultSpacing);

    .email-input {
        display: flex;
        flex-direction: row;
        gap: var(--largeSpacing);
        align-items: center;
    }
}
</style>
